import { faFileImport, faInfoCircle, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { action } from 'mobx';
import { Observer, observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { EmailBaseCampaign } from '@feathr/blackbox';
import { CampaignClass, getSegmentsUniqueEmailsPayload } from '@feathr/blackbox';
import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  CardV2,
  Checkbox,
  EmptyState,
  Fieldset,
  Text,
  Tooltip,
} from '@feathr/components';
import ConfirmButton from '@feathr/extender/components/ConfirmButton';
import type { ISegmentSelectChangeProps } from '@feathr/extender/components/SegmentSelect';
import SegmentSelect from '@feathr/extender/components/SegmentSelect';
import { useUniqueEmails } from '@feathr/extender/hooks';
import { useFlags, useStore } from '@feathr/extender/state';

import { useContactsImport } from '../CampaignEditPage.useContactsImport';

import * as styles from './EmailCampaignSegments.css';

interface IProps {
  campaign: EmailBaseCampaign;
  disabled: boolean;
  hideIncluded?: boolean;
  showImportContacts?: boolean;
  showSendAll?: boolean;
  step?: number;
  title?: string;
  disabledTooltip?: string;
}

function EmailCampaignSegments({
  campaign,
  disabled,
  hideIncluded = false,
  showImportContacts = true,
  showSendAll = true,
  step,
  title,
  disabledTooltip,
}: Readonly<IProps>): JSX.Element {
  const { Goals, Targetings } = useStore();
  const segments = campaign.get('segments', []);
  const flags = useFlags();
  const { t } = useTranslation();
  const { addImporter, getRedirect } = useContactsImport(campaign);

  const isExcluded = [CampaignClass.Drip, CampaignClass.AutoPinpointEmail].includes(
    campaign.get('_cls'),
  );
  const filteredSegments = !isExcluded ? segments : segments.filter((s) => !s.included);
  const shouldShowAdditional = isExcluded && segments.filter((s) => !s.included).length >= 1;

  // The count must follow unsaved group filter edits, which the payload sends inline, and the
  // reaction re-runs on every observable the payload reads — including the campaign's groups.
  const uniqueEmailsCount = useUniqueEmails(
    (Segments) => {
      const { excludedSegmentIds, includedSegmentIds } = campaign.audienceSegmentIds;
      if (!excludedSegmentIds.length && !includedSegmentIds.length) {
        return null;
      }
      return getSegmentsUniqueEmailsPayload(Segments, campaign);
    },
    [campaign],
  )?.unique_emails;
  // The count is unknown (undefined) until the first request resolves, and again after a failed
  // request; the em-dash is the shared empty-value convention.
  const countDisplay = uniqueEmailsCount?.toLocaleString() ?? '—';

  function handleChangeSegment({ included, oldId, id }: ISegmentSelectChangeProps): void {
    const i = segments.findIndex((s) => s.id === oldId);
    if (i === -1) {
      // This should not happen.
      return;
    }

    action(() => {
      segments[i].id = id;
      segments[i].included = included;

      campaign.set({ segments: segments });
      campaign.setAttributeDirty('segments');
    })();
  }

  function handleRemoveSegment(id?: string): void {
    if (!id) {
      const segment = segments.find((s) => !s.id);
      if (segment) {
        segments.remove(segment);
      }
    } else {
      campaign.set({
        segments: campaign.get('segments', []).filter((s) => s.id !== id),
      });
    }
    if (!campaign.get('segments', []).length) {
      campaign.set({ consent: { has_consent: false } });
    }
  }

  function handleChangeEndpointType(checked?: boolean): void {
    campaign.set({ endpoint_type: checked ? 'all_emails' : 'primary_email' });
  }

  useEffect(() => {
    if (segments.length === 0) {
      handleChangeEndpointType(false);
    }
  }, [segments.length]);

  function addSegment(): void {
    action(() => {
      segments.push({ id: '', included: !isExcluded });
    })();
  }

  function getAddSegmentButtonText(): string {
    if (isExcluded && shouldShowAdditional) {
      return t('Add additional group');
    }
    if (!isExcluded && segments.length >= 1) {
      return t('Add additional group');
    }
    return t('Add group');
  }

  return (
    <CardV2 width={'full'}>
      <CardHeader title={title ?? t('Groups')}>
        {showImportContacts && (
          <ConfirmButton
            description={t(
              'Before you can import contacts, the changes to your campaign need to be saved.',
            )}
            disabled={disabled}
            model={campaign}
            name={'import_contacts'}
            onConfirm={addImporter}
            prefix={<FontAwesomeIcon icon={faFileImport} />}
            redirect={getRedirect}
            step={step}
          >
            {t('Import contacts')}
          </ConfirmButton>
        )}
      </CardHeader>
      <CardContent addVerticalGap={true}>
        <>
          {segments.length === 0 && <EmptyState label={t('No groups added')} theme={'neutral'} />}
          {!!segments.length && (
            <Fieldset className={styles.segment}>
              {filteredSegments.map((segment) => (
                <Observer key={segment.id || 'add'}>
                  {(): JSX.Element => {
                    const affectedTargetings = Targetings.list({
                      filters: {
                        _parent__ne: campaign.id,
                        _target_data: segment.id || undefined,
                      },
                      pagination: {
                        distinct: 'parent',
                      },
                    });
                    const affectedGoals = Goals.list({
                      filters: { segment: segment.id || undefined },
                    });
                    return (
                      <SegmentSelect
                        affectedCampaigns={affectedTargetings.pagination.distinct}
                        affectedGoals={affectedGoals.pagination.count}
                        disabled={disabled}
                        disableInclusion={isExcluded}
                        emailCampaign={campaign.isEmailSingleSend ? campaign : undefined}
                        excludeIds={segments.filter((s) => s.id !== undefined).map((s) => s.id!)}
                        hideIncluded={hideIncluded}
                        included={segment.included}
                        name={'segment_select'}
                        onChange={handleChangeSegment}
                        onRemove={handleRemoveSegment}
                        stat={'num_emails_total'}
                        value={segment.id}
                      />
                    );
                  }}
                </Observer>
              ))}
            </Fieldset>
          )}
          {flags.pinpointSendAllEmails && showSendAll && (
            <Checkbox
              className={styles.endpointType}
              // Checkbox is only active when at least one target group is added
              disabled={segments.length === 0}
              label={t(
                'Some targeted people may have multiple email addresses. Send this email to every email address stored for every targeted person.',
              )}
              layout={'well'}
              onChange={handleChangeEndpointType}
              value={campaign.get('endpoint_type') === 'all_emails'}
            />
          )}
        </>
      </CardContent>
      <CardActions contentClassName={styles.cardActions}>
        <Text.DefaultMedium mantineComponent={'span'}>
          {t('Total Unique Contacts: {{num}} ', { num: countDisplay })}
          {segments.length > 1 && (
            <Tooltip title={t('The number of unique emails between all selected groups.')}>
              <FontAwesomeIcon icon={faInfoCircle} />
            </Tooltip>
          )}
        </Text.DefaultMedium>
        <Button
          disabled={disabled || segments.some((s) => !s.id)}
          name={'add_segment'}
          onClick={addSegment}
          prefix={<FontAwesomeIcon icon={faPlus} />}
          tooltip={disabledTooltip}
        >
          {getAddSegmentButtonText()}
        </Button>
      </CardActions>
    </CardV2>
  );
}

export default observer(EmailCampaignSegments);
